import React, { useEffect, useState } from "react";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IconFileSymlink,
  IconEdit,
  IconTrash,
  IconPencil,
  IconCircleX,
} from "@tabler/icons-react";
import MAvatar from "@/components/ui/atoms/MAvatar";
import MOptionsMenu from "@/components/ui/atoms/MOptionsMenu";
import { useNotes } from "@/hooks/useNotes";
import MLoader from "@/components/ui/atoms/MLoader";
import styles from "@/pages/documents/document.module.css";
import { formatDocumentDateShort, formatCommentDate } from "@/utils/date-utils";
import NoteDrawer from "@/pages/documents/notes/NoteDrawer";
import MDialog from "@/components/ui/atoms/MDialog";
import { getMemberInfo } from "@/hooks/useMembers";
import ThreadNote from "@/pages/profile/family/notes/ThreadNote";
import { MButton } from "@/components/ui/atoms/MButtons";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import MTextArea from "@/components/ui/atoms/MTextArea";

const FamilyNoteDetail: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const noteId = searchParams.get("noteId") || undefined;
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const loggedInMemberId = JSON.parse(
    localStorage.getItem("member") || "{}"
  ).id;
  const selectedMemberId = memberInfo.id || "";
  const isSelf = loggedInMemberId === selectedMemberId;
  const {
    fetchNote,
    updateNote,
    deleteNote,
    addComment,
    updateComment,
    deleteComment,
    loading,
  } = useNotes(selectedMemberId);
  const [note, setNote] = useState<any>(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [threadInput, setThreadInput] = useState("");
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  // Edit thread drawer state
  const [editThreadOpen, setEditThreadOpen] = useState(false);
  const [editThreadComment, setEditThreadComment] = useState<any>(null);
  const [editThreadText, setEditThreadText] = useState("");
  // Delete thread dialog state
  const [deleteThreadOpen, setDeleteThreadOpen] = useState(false);
  const [deleteThreadComment, setDeleteThreadComment] = useState<any>(null);

  const handleThreadSend = async () => {
    if (!noteId || !threadInput.trim()) return;
    await addComment(noteId, threadInput.trim());
    //toast.showToast({ message: 'Comment added', variant: 'success' });
    const updated = await fetchNote(noteId);
    setNote(updated);
    setThreadInput("");
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  // Auto-resize textarea as content grows
  const handleThreadInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setThreadInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (noteId) {
      fetchNote(noteId).then((data) => setNote(data));
    }
  }, [noteId, fetchNote]);

  if (loading) return <MLoader />;
  if (!note) return <div className={styles.noteNotFound}>Note not found</div>;

  return (
    <div
      className={styles.familyNotePageRoot}
      style={{ paddingBottom: "calc(140px + env(safe-area-inset-bottom))" }}
    >
      <div className={styles.familyNoteHeaderRow}>
        <div className={styles.familyNoteHeaderTextWrap}>
          <div className="heading-h7-bold">{note.title}</div>
          <div className={styles.familyNoteDateRow}>
            {note.updatedAt
              ? `Last Updated ${formatCommentDate(note.updatedAt)}`
              : ""}
          </div>
        </div>
        <div className={styles.familyNoteActions}>
          {loggedInMemberId === note.ownerId && (
            <>
              <IconPencil
                size={22}
                className={styles.familyNoteActionIcon}
                onClick={() => setShowDrawer(true)}
                title="Edit Note"
              />
              <IconTrash
                size={22}
                className={`${styles.familyNoteActionIcon} ${styles.delete}`}
                onClick={() => setShowDeleteDialog(true)}
                title="Delete Note"
              />
            </>
          )}
        </div>
      </div>
      <div className="family-note-detail-frame">
        <div className="family-note-detail-meta">
          <MAvatar
            size={24}
            type="no-dp"
            text={`${note.ownerFirstName} ${note.ownerLastName}`}
            textSize={11}
          />
          <div className="family-note-detail-meta-text">
            <span className="family-note-detail-meta-owner body-base-bold">
              {note.ownerId === loggedInMemberId
                ? "You"
                : note.ownerFirstName || "Member"}
            </span>
            <span className="family-note-detail-meta-date">
              {note.createdAt ? formatCommentDate(note.createdAt) : ""}
            </span>
          </div>
        </div>
        <div className="family-note-detail-content">{note.content}</div>
      </div>

      {/* Render thread comments */}
      {Array.isArray(note.comments) && note.comments.length > 0 && (
        <div className="family-note-thread-section">
          {note.comments.map((comment: any, idx: number) => (
            <div key={comment.uid || idx} style={{ width: "100%" }}>
              <div className="family-note-thread-meta">
                <MAvatar
                  size={24}
                  type="no-dp"
                  text={`${comment.member.first_name} ${comment.member.last_name}`}
                  textSize={11}
                />
                <span className="family-note-thread-meta-owner">
                  {comment.member?.id === loggedInMemberId
                    ? "You"
                    : comment.member?.first_name || "Member"}
                </span>
                <span className="family-note-thread-meta-date">
                  {comment.updatedAt
                    ? formatCommentDate(comment.updatedAt)
                    : ""}
                </span>
                {comment.member?.id === loggedInMemberId && (
                  <MOptionsMenu
                    options={[
                      {
                        id: "edit",
                        label: "Edit",
                        icon: <IconPencil size={18} />,
                        onClick: () => {
                          setEditThreadOpen(true);
                          setEditThreadComment(comment);
                          setEditThreadText(comment.content);
                        },
                      },
                      {
                        id: "delete",
                        label: "Delete",
                        icon: <IconCircleX size={18} />,
                        delete: true,
                        onClick: () => {
                          setDeleteThreadOpen(true);
                          setDeleteThreadComment(comment);
                        },
                      },
                    ]}
                  />
                )}
              </div>
              <div className="family-note-thread-comment">
                <div className="family-note-thread-comment-text">
                  {comment.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add to this thread input */}
      <div
        className="family-note-thread-footer"
        style={{ alignItems: "flex-end" }}
      >
        <div style={{ flex: 1 }}>
          <MTextArea
            ref={textareaRef}
            placeholder="Add to this thread"
            value={threadInput || ""}
            onChange={handleThreadInputChange}
            disabled={loading}
            rows={1}
            style={{ height: "auto", maxHeight: "200px", overflow: "auto" }}
          />
        </div>
        <MButton
          size="md"
          variant="icon-only-filled"
          icon="send-2"
          aria-label="Send"
          disabled={!threadInput.trim()}
          onClick={handleThreadSend}
        />
      </div>

      {/* Edit Thread Drawer */}
      {editThreadOpen && (
        <FooterDrawer
          open={editThreadOpen}
          onClose={() => setEditThreadOpen(false)}
          title="Edit Thread"
          onDone={async () => {
            if (noteId && editThreadComment && editThreadText.trim()) {
              await updateComment(
                noteId,
                editThreadComment.uid ||
                  editThreadComment.id ||
                  editThreadComment._id,
                editThreadText.trim()
              );
              //toast.showToast({ message: 'Comment updated', variant: 'success' });
              const updated = await fetchNote(noteId);
              setNote(updated);
            }
            setEditThreadOpen(false);
            setEditThreadComment(null);
            setEditThreadText("");
          }}
          doneDisabled={!editThreadText.trim() || loading}
        >
          <div style={{ padding: 16 }}>
            <MTextArea
              value={editThreadText}
              onChange={(e) => setEditThreadText(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Edit your comment"
              autoFocus
            />
          </div>
        </FooterDrawer>
      )}

      {/* Delete Thread Dialog */}
      <MDialog
        isOpen={deleteThreadOpen}
        onClose={() => setDeleteThreadOpen(false)}
        title="Delete this comment?"
        description="This comment will be permanently removed from the family note."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={async () => {
          if (noteId && deleteThreadComment) {
            await deleteComment(
              noteId,
              deleteThreadComment.uid ||
                deleteThreadComment.id ||
                deleteThreadComment._id
            );
            //toast.showToast({ message: 'Comment deleted', variant: 'success' });
            const updated = await fetchNote(noteId);
            setNote(updated);
          }
          setDeleteThreadOpen(false);
          setDeleteThreadComment(null);
        }}
        onSecondaryCta={() => setDeleteThreadOpen(false)}
      />

      {/* Note Drawer for edit */}
      {showDrawer && (
        <NoteDrawer
          open={showDrawer}
          onClose={() => setShowDrawer(false)}
          onDone={async (fields) => {
            if (noteId) {
              await updateNote(noteId, fields);
              //toast.showToast({ message: 'Note updated', variant: 'success' });
              const updated = await fetchNote(noteId);
              setNote(updated);
            }
            setShowDrawer(false);
          }}
          loading={loading}
          noteId={noteId}
          initialData={{
            type: "family",
            title: note.title,
            content: note.content,
          }}
          mode="edit"
          disableTypeToggle={true}
          noteType="family"
        />
      )}
      {/* Delete dialog */}
      <MDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Delete this note?"
        description="This note and all its comments will be permanently removed."
        primaryCtaText="Yes"
        secondaryCtaText="No"
        onPrimaryCta={async () => {
          if (noteId) {
            await deleteNote(noteId);
            //toast.showToast({ message: 'Note deleted', variant: 'success' });
            setShowDeleteDialog(false);
            navigate(-1); // Go back to notes list
          }
        }}
        onSecondaryCta={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};

export default FamilyNoteDetail;
